import { MouseEvent, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDialog } from "providers/DialogProvider";

import { Box, LinearProgress, Menu, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DataGrid, GridColumns, GridRowData, GridRowId, GridSortModel, GridOverlay } from "@mui/x-data-grid";

import { MoreMenuItems } from "./MoreMenuItems";
import {
  SortSportEquipmentsColumnsEnum
} from "store/reactiveVarsStores/spor-equipmnts/sort-sport-equipments-columns.enum";
import { SportEquipmentListSortOrder, SportEquipmentListSortOrderInitial } from "store/reactiveVarsStores/spor-equipmnts/sportEquipmentListSortOrder";
import { SportEquipmentListPagination } from "store/reactiveVarsStores/spor-equipmnts/sportEquipmentListPagination";
import { SportEquipmentTab } from "store/reactiveVarsStores/spor-equipmnts/sportEquipmentTabActive";
import { SportEquipmentEntity } from "graphql/graphQlApiHooks";
import { Footer } from "../Footer";
import { SportEquipmentsChangeСategory } from "../Footer/SportEquipmentsChangeСategory";
import { SportEquipmentsRelocate } from "../Footer/SportEquipmentsRelocate";
import { SortDirectionEnum } from "../../../../../../store/reactiveVarsStores/spor-equipmnts/sort-direction.enum";
import GroupDeleteSportEquipments from "../Footer/GroupDeleteSportEquipments";
import {
  NameCell,
  TechnicalConditionCell,
  SportZoneCell,
  SportSubZoneCell,
  DateCell,
  ServicePeriodsCell,
  ActionsCell
} from "./CellRenderers";


interface Props {
  allCount: number;
  loading: boolean;
  sportEquipments: SportEquipmentEntity[];
  pagination: SportEquipmentListPagination;
  tabActive: SportEquipmentTab;
  sortOrderModel: SportEquipmentListSortOrder;

  actions: {
    setPagination: (pagination: SportEquipmentListPagination) => void;
    setOrder: (orderModel: SportEquipmentListSortOrder) => void;
  };
}

const StyledGridOverlay = styled(GridOverlay)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  '& .empty-icon': {
    width: 120,
    height: 120,
    marginBottom: theme.spacing(3),
    opacity: 0.6,
    color: theme.palette.mode === 'light' ? theme.palette.grey[400] : theme.palette.grey[600],
  },
  '& .empty-title': {
    fontSize: '18px',
    fontWeight: 500,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  '& .empty-description': {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    maxWidth: 400,
  },
}));

const NoRowsOverlay = () => (
  <StyledGridOverlay>
    <svg
      className="empty-icon"
      viewBox="0 0 200 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g opacity="0.7">
        {/* Magnifying glass */}
        <circle cx="85" cy="75" r="35" stroke="currentColor" strokeWidth="2.5" fill="none" />
        <path
          d="M110 100L130 120"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Document lines */}
        <rect x="30" y="130" width="140" height="6" rx="3" fill="currentColor" opacity="0.4" />
        <rect x="30" y="142" width="120" height="6" rx="3" fill="currentColor" opacity="0.3" />
        <rect x="30" y="154" width="100" height="6" rx="3" fill="currentColor" opacity="0.2" />
      </g>
    </svg>
    <Typography className="empty-title">Данные не найдены</Typography>
    <Typography className="empty-description">
      По выбранным критериям не найдено ни одной записи
    </Typography>
  </StyledGridOverlay>
);

// TODO:
//    добавить фильтрацию по полю при наведении на ячейку

export const List = (props: Props) => {
  const { sportEquipments, pagination, allCount, loading, tabActive, actions: { setPagination, setOrder } } = props;

  const navigate = useNavigate();
  const [dialog, closeDialog] = useDialog();

  const [selected, setSelected] = useState<GridRowId[]>([]);

  // Мемоизированный обработчик для навигации к графикам обслуживания
  const handleServicePeriodsNavigate = useCallback((id: string) => {
    navigate("service-periods/" + id);
  }, [navigate]);

  // Мемоизированный массив колонок
  const sportEquipmentsColumns: GridColumns<SportEquipmentEntity> = useMemo(() => [
    {
      field: SortSportEquipmentsColumnsEnum.NAME,
      headerName: "Название",
      renderCell: (params) => <NameCell row={params.row} />,
      minWidth: 300,
      flex: 2.5,
      disableColumnMenu: true,
      editable: false
    },
    {
      field: SortSportEquipmentsColumnsEnum.TECHNICAL_CONDITION,
      headerName: "Техническое состояние",
      flex: .7,
      editable: false,
      renderCell: (params) => <TechnicalConditionCell row={params.row} />,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
      disableColumnMenu: true
    },
    {
      field: SortSportEquipmentsColumnsEnum.SPORT_ZONE,
      headerName: "Здание/Помещение",
      renderCell: (params) => <SportZoneCell row={params.row} tabActiveId={tabActive?.id} />,
      flex: 1.5,
      editable: false,
      minWidth: 220,
      disableColumnMenu: true
    },
    {
      field: SortSportEquipmentsColumnsEnum.SPORT_SUBZONE,
      headerName: "Спортзона",
      renderCell: (params) => <SportSubZoneCell row={params.row} tabActiveId={tabActive?.id} />,
      flex: 1.5,
      editable: false,
      minWidth: 220,
      disableColumnMenu: true
    },
    {
      field: SortSportEquipmentsColumnsEnum.COMMISSIONING_DATE,
      headerName: "Дата ввода",
      renderCell: (params) => <DateCell row={params.row} field="commissioningDate" />,
      flex: .5,
      minWidth: 120,
      editable: false,
      disableColumnMenu: true
    },
    {
      field: SortSportEquipmentsColumnsEnum.CREATED_AT,
      headerName: "Дата добавления",
      renderCell: (params) => <DateCell row={params.row} field="createdAt" />,
      flex: .5,
      editable: false,
      minWidth: 120,
      disableColumnMenu: true
    },
    {
      field: "servicePeriods", headerName: "График тех. обслуживания",
      renderCell: (params) => <ServicePeriodsCell row={params.row} onNavigate={handleServicePeriodsNavigate} />,
      flex: .5,
      editable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      align: "center"
    },
    {
      field: "actions", headerName: "Действия",
      renderCell: (params) => <ActionsCell row={params.row} />,
      disableColumnMenu: true,
      flex: 0,
      editable: false,
      sortable: false,
      hideSortIcons: true,
      align: "center",
      headerAlign: "center"
    }
  ], [tabActive?.id, handleServicePeriodsNavigate]);

  const [contextRow, setContextRow] = useState<SportEquipmentEntity | null>(null);

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const contextMenuHandler = useCallback((event: MouseEvent) => {
    event.preventDefault();
    const row = event.currentTarget;
    const sportEquipmentId = row.getAttribute("data-id");
    setContextRow(
      sportEquipments.find((item) =>
        item.id === sportEquipmentId
      ) || null
    );
    setContextMenu((prevContextMenu) =>
      prevContextMenu === null
        ? {
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6
        }
        :
        null
    );
  }, [sportEquipments]);

  const contextMenuCloseHandler = useCallback(() => {
    setContextMenu(null);
  }, []);


  const onSportEquipmentsRelocateHandler = useCallback(() => {
    // Получаем данные о выбранном оборудовании для проверки статуса inUse
    const selectedEquipmentsData = sportEquipments.filter(eq => selected.includes(eq.id));
    
    dialog({
      onClose: closeDialog,
      title: "Перемещение оборудования",
      children: <>
        <SportEquipmentsRelocate
          equipments={selected as string[]}
          equipmentsData={selectedEquipmentsData}
          close={() => {
            setSelected([]);
            closeDialog();
          }}
        />
      </>
    });
  }, [sportEquipments, selected, dialog, closeDialog]);

  const onCategoryChangeHandler = useCallback(() => {
    dialog({
      onClose: closeDialog,
      title: "Изменение категории",
      children: <>
        <SportEquipmentsChangeСategory
          equipments={selected as string[]}
          close={() => {
            setSelected([]);
            closeDialog();
          }}
        />
      </>
    });
  }, [selected, dialog, closeDialog]);

  const onGroupDeleteHandler = useCallback(() => {
    dialog({
      onClose: closeDialog,
      title: "Удаление оборудования",
      children: <>
        <GroupDeleteSportEquipments
          close={closeDialog}
          onSubmitted={() => {
            setSelected([])
            closeDialog();
          }}
          equipments={selected as string[]}
        />
      </>
    });
  }, [selected, dialog, closeDialog]);

  const doubleClickHandler = useCallback((id: string) => {
    //TODO вынести в action
    navigate(id);
  }, [navigate]);

  const sortModelChangeHandler = useCallback((model: GridSortModel) => {
    const sort = model.map((item) => {
      return {
        [item.field]: item.sort.toUpperCase() as SortDirectionEnum
      } as SportEquipmentListSortOrder;
    });
    setOrder(sort[0] || SportEquipmentListSortOrderInitial);
  }, [setOrder]);

  const handleSelectionChange = useCallback((newSelection: GridRowId[]) => {
    setSelected(newSelection);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPagination({ ...pagination, page: page });
  }, [pagination, setPagination]);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ ...pagination, pageSize: pageSize });
  }, [pagination, setPagination]);

  // Показываем кастомный компонент, когда нет данных и не идет загрузка
  const showEmptyState = !loading && sportEquipments.length === 0;

  return (
    <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
      <DataGrid
        rows={sportEquipments}
        columns={sportEquipmentsColumns}
        getRowClassName={({ id }) => (id === contextRow?.id && contextMenu !== null) ? "isOpenContextMenu" : ""}
        loading={loading}

        sx={{
          "& .isOpenContextMenu": {
            bgcolor: "action.hover"
          },
          ...(showEmptyState && {
            "& .MuiDataGrid-virtualScroller": {
              minHeight: "400px !important",
            },
            "& .MuiDataGrid-overlayWrapper": {
              height: "400px !important",
            },
          }),
        }}

        componentsProps={{
          footer: {
            selected,
            onSportEquipmentsRelocateHandler: onSportEquipmentsRelocateHandler,
            onCategoryChangeHandler: onCategoryChangeHandler,
            onGroupDeleteHandler: onGroupDeleteHandler
          },
          row: {
            onContextMenu: contextMenuHandler,
            style: { cursor: "context-menu" }
          }
        }}
        components={{
          Footer: Footer,
          LoadingOverlay: LinearProgress,
          NoRowsOverlay: NoRowsOverlay,
        }}

        onRowDoubleClick={(params: GridRowData) => doubleClickHandler(params.row.id)}

        sortingMode='server'
        onSortModelChange={sortModelChangeHandler}

        checkboxSelection
        disableSelectionOnClick={true}

        selectionModel={selected}
        onSelectionModelChange={handleSelectionChange}

        getRowId={(row) => row.id}
        getRowHeight={() => 50}
        columnBuffer={3}
        columnThreshold={3}
        rowBuffer={5}
        rowThreshold={5}
        disableExtendRowFullWidth
        scrollbarSize={8}

        pagination
        paginationMode='server'
        rowCount={showEmptyState ? 0 : Math.round(allCount)}
        page={pagination?.page}
        pageSize={pagination?.pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        rowsPerPageOptions={[5, 10, 50, 100]}
      />

      <Menu
        open={contextMenu !== null}
        onClose={contextMenuCloseHandler}
        anchorReference='anchorPosition'
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        elevation={22}
        sx={{
          maxWidth: 300,
          "& .MuiListItemText-root .MuiTypography-root": {
            textOverflow: "ellipsis",
            overflow: "hidden",
            width: "100%",
            display: "flow-root"
          }
        }}
      >
        {contextRow && <MoreMenuItems
          sportEquipment={contextRow}
          onClose={contextMenuCloseHandler}
        />}
      </Menu>
    </Box>
  );
};